import React from "react";
import {Autocomplete, TextField} from "@mui/material";
import {useTranslation} from "react-i18next";
import useDebounce from "../../hooks/useDebounce.ts";
import supportContextService, {type SupportContextOption} from "../../service/supportContextService.ts";
import {useSupportContext} from "../../hooks/useSupportContext.ts";
import {devError} from "../../utils/dev.ts";

const MIN_SEARCH_LENGTH = 2;

interface SupportContextPickerProps {
    className?: string;
    size?: "small" | "medium";
    autoFocus?: boolean;
}

/** Lets support pick a garage or group to work in; only rendered for the support context permission. */
const SupportContextPicker = ({className = "w-full sm:w-80", size = "small", autoFocus}: SupportContextPickerProps) => {
    const {t} = useTranslation();
    const {enter} = useSupportContext();
    const [input, setInput] = React.useState("");
    const [options, setOptions] = React.useState<SupportContextOption[]>([]);
    const [loading, setLoading] = React.useState(false);
    const debouncedInput = useDebounce(input, 400);

    React.useEffect(() => {
        if (debouncedInput.trim().length < MIN_SEARCH_LENGTH) {
            setOptions([]);
            return;
        }
        let active = true;
        setLoading(true);
        supportContextService.search(debouncedInput.trim())
            .then(found => { if (active) setOptions(found); })
            .catch(err => { devError("Support context search failed", err); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [debouncedInput]);

    return (
        <Autocomplete<SupportContextOption>
            className={className}
            size={size}
            options={options}
            filterOptions={found => found}
            loading={loading || (input.trim().length >= MIN_SEARCH_LENGTH && input !== debouncedInput)}
            loadingText={t("supportContext.loading")}
            noOptionsText={t("supportContext.noOptions")}
            groupBy={option => t(`supportContext.${option.type}`)}
            getOptionLabel={option => [option.customerNo, option.label].filter(Boolean).join(" ")}
            isOptionEqualToValue={(option, value) => option.type === value.type && option.no === value.no}
            value={null}
            inputValue={input}
            onInputChange={(_event, value, reason) => { if (reason !== "reset") setInput(value); }}
            onChange={(_event, option) => {
                if (!option) return;
                setInput("");
                enter({type: option.type, no: option.no, label: option.label});
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label={t("supportContext.searchLabel")}
                    placeholder={t("supportContext.searchPlaceholder")}
                    autoFocus={autoFocus}
                    slotProps={{
                        ...params.slotProps,
                        htmlInput: {
                            ...params.slotProps.htmlInput
                        }
                    }}
                />
            )}
        />
    );
};

export default SupportContextPicker;
